// The animated bits as single elements, each animated on the compositor
// (transform and opacity through the Web Animations API) and removed when
// it is done. Positions are pixels inside the sky stage.
import type { AmbientConfig } from './ambientConfig';
import type { Point, Segment } from './schedule';

function lerp(a: Point, b: Point, t: number): Point {
  return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
}

function removeWhenDone(anim: Animation, el: Element): Animation {
  const remove = () => el.remove();
  anim.addEventListener('finish', remove);
  anim.addEventListener('cancel', remove);
  return anim;
}

/** A shooting star: a short line with a fading tail, its head running along the path. top is the half's top in px. */
export function spawnShootingStar(container: HTMLElement, path: Segment, top: number, cfg: AmbientConfig['shootingStar'], scale: number): Animation {
  const length = cfg.length * scale;
  const el = document.createElement('div');
  el.className = 'ambient-meteor';
  el.style.width = `${length}px`;
  el.style.height = `${cfg.width}px`;
  el.style.transformOrigin = `${length}px 50%`;
  container.appendChild(el);
  const angle = (Math.atan2(path.to.y - path.from.y, path.to.x - path.from.x) * 180) / Math.PI;
  const at = (p: Point, tail: number) => `translate(${p.x - length}px, ${top + p.y - cfg.width / 2}px) rotate(${angle}deg) scaleX(${tail})`;
  const anim = el.animate(
    [
      { transform: at(path.from, 0.1), opacity: 0 },
      { transform: at(lerp(path.from, path.to, 0.2), 0.6), opacity: cfg.brightness, offset: 0.2 },
      { transform: at(lerp(path.from, path.to, 0.7), 1), opacity: cfg.brightness * 0.8, offset: 0.7 },
      { transform: at(path.to, 1), opacity: 0 },
    ],
    { duration: cfg.durationMs, easing: 'linear' },
  );
  return removeWhenDone(anim, el);
}

/** A satellite: a faint steady point crossing the whole view. */
export function spawnSatellite(container: HTMLElement, path: Segment, top: number, cfg: AmbientConfig['satellite']): Animation {
  const el = document.createElement('div');
  el.className = 'ambient-satellite';
  el.style.width = `${cfg.size}px`;
  el.style.height = `${cfg.size}px`;
  container.appendChild(el);
  const at = (p: Point) => `translate(${p.x - cfg.size / 2}px, ${top + p.y - cfg.size / 2}px)`;
  const anim = el.animate(
    [
      { transform: at(path.from), opacity: 0 },
      { opacity: cfg.brightness, offset: 0.06 },
      { opacity: cfg.brightness, offset: 0.94 },
      { transform: at(path.to), opacity: 0 },
    ],
    { duration: cfg.crossingS * 1000, easing: 'linear' },
  );
  return removeWhenDone(anim, el);
}

/**
 * A twinkle: one background star dims a little, then swells brighter and
 * larger, and settles. Slow, never a flash.
 */
export function twinkleStar(el: Element, base: number, cfg: AmbientConfig['twinkle'], durationMs: number): Animation {
  const low = base * (1 - cfg.depth / 2);
  const high = base + (1 - base) * cfg.depth;
  return el.animate(
    [
      { opacity: base, transform: 'scale(1)' },
      { opacity: low, transform: 'scale(1)', offset: 0.3 },
      { opacity: high, transform: `scale(${cfg.scale})`, offset: 0.65 },
      { opacity: base, transform: 'scale(1)' },
    ],
    { duration: durationMs, easing: 'ease-in-out' },
  );
}
